import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Search, 
  Library, 
  Eye, 
  EyeOff,
  Calendar, 
  User, 
  Building,
  Play,
  FileText,
  Grid3X3,
  List,
  Image,
  ArrowUpDown,
  Check,
  X,
  Shield,
  ShieldOff,
  CheckSquare,
  Square,
  MoreVertical,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  BarChart3,
  Award
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLibrary() {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [visibilityFilter, setVisibilityFilter] = useState("all"); // all, public, private
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Quick Preview state
  const [previewSession, setPreviewSession] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewSlides, setPreviewSlides] = useState([]);
  const [previewFileType, setPreviewFileType] = useState(null);
  const [previewNumPages, setPreviewNumPages] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewScale, setPreviewScale] = useState(1.0);

  useEffect(() => {
    fetchPresentations();
    fetchDepartments();
  }, []);

  const fetchPresentations = async () => {
    setLoading(true);
    try {
      // Admin gets all presentations including private ones
      const response = await axios.get(`${API}/library/presentations?include_private=true`);
      setPresentations(response.data.presentations || []);
    } catch (error) {
      console.error("Failed to fetch presentations:", error);
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/analytics/reports`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  const toggleVisibility = async (sessionId, currentVisibility) => {
    try {
      await axios.put(`${API}/library/presentations/${sessionId}/visibility?is_public=${!currentVisibility}`);
      setPresentations(prev => prev.map(p => 
        p.id === sessionId ? { ...p, is_public: !currentVisibility } : p
      ));
      toast.success(`Presentation ${!currentVisibility ? 'shown' : 'hidden'} in library`);
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Failed to update visibility");
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredPresentations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredPresentations.map(p => p.id));
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one presentation");
      return;
    }

    setBulkLoading(true);
    try {
      await axios.post(`${API}/library/presentations/bulk-action`, {
        session_ids: selectedIds,
        action: action
      });
      
      // Update local state
      setPresentations(prev => prev.map(p => 
        selectedIds.includes(p.id) ? { ...p, is_public: action === 'activate' } : p
      ));
      
      setSelectedIds([]);
      toast.success(`Successfully ${action === 'activate' ? 'activated' : 'deactivated'} ${selectedIds.length} presentations`);
    } catch (error) {
      console.error("Bulk action failed:", error);
      toast.error("Bulk action failed");
    } finally {
      setBulkLoading(false);
    }
  };

  // Quick Preview functions
  const openQuickPreview = async (presentation) => {
    setPreviewSession(presentation);
    setPreviewLoading(true);
    setPreviewPage(1);
    setPreviewScale(1.0);
    
    try {
      const response = await axios.get(`${API}/sessions/${presentation.id}/slides`);
      setPreviewSlides(response.data.slides || []);
      setPreviewFileType(response.data.file_type);
      
      if (response.data.pdf_url) {
        // pdf_url is like /api/sessions/.../pdf-view - append to backend URL
        setPreviewPdfUrl(`${process.env.REACT_APP_BACKEND_URL}${response.data.pdf_url}`);
      } else if (response.data.file_type === 'pdf') {
        setPreviewPdfUrl(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/${presentation.id}/presentation-file`);
      } else {
        setPreviewPdfUrl(null);
      }
    } catch (error) {
      console.error("Failed to load preview:", error);
      toast.error("Failed to load presentation preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeQuickPreview = () => {
    setPreviewSession(null);
    setPreviewPdfUrl(null);
    setPreviewSlides([]);
    setPreviewFileType(null);
    setPreviewNumPages(null);
  };

  const onPreviewDocumentLoadSuccess = useCallback(({ numPages }) => {
    setPreviewNumPages(numPages);
    setPreviewPage(1);
  }, []);

  const changePreviewPage = (delta) => {
    if (previewFileType === 'pdf' && previewNumPages) {
      setPreviewPage(prev => Math.min(Math.max(1, prev + delta), previewNumPages));
    } else if (previewSlides.length > 0) {
      setPreviewPage(prev => Math.min(Math.max(1, prev + delta), previewSlides.length));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter presentations
  let filteredPresentations = presentations;
  
  if (searchQuery) {
    filteredPresentations = filteredPresentations.filter(p =>
      p.topic_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.concept_overview?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  
  if (selectedDepartment !== "all") {
    filteredPresentations = filteredPresentations.filter(p => p.department === selectedDepartment);
  }
  
  if (visibilityFilter !== "all") {
    filteredPresentations = filteredPresentations.filter(p => 
      visibilityFilter === "public" ? p.is_public !== false : p.is_public === false
    );
  }

  // Sort presentations
  const sortedPresentations = [...filteredPresentations].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === "oldest") {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (sortBy === "most_viewed") {
      return (b.view_count || 0) - (a.view_count || 0);
    }
    return 0;
  });

  // Group by month
  const groupedByMonth = sortedPresentations.reduce((acc, p) => {
    const month = p.month || "Unknown";
    if (!acc[month]) acc[month] = [];
    acc[month].push(p);
    return acc;
  }, {});

  const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => b.localeCompare(a));

  const publicCount = presentations.filter(p => p.is_public !== false).length;
  const privateCount = presentations.filter(p => p.is_public === false).length;

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>
            <Library className="inline w-7 h-7 mr-2 text-primary" />
            Presentation Library Management
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Manage visibility and access for {presentations.length} presentations
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Stats badges */}
          <Badge variant="outline" className="px-3 py-1">
            <Eye className="w-3 h-3 mr-1" />
            {publicCount} Public
          </Badge>
          <Badge variant="outline" className="px-3 py-1">
            <EyeOff className="w-3 h-3 mr-1" />
            {privateCount} Hidden
          </Badge>
          
          {/* View Toggle */}
          <div className="flex items-center gap-1 border rounded-full p-1">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="rounded-full h-8 w-8 p-0"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-full h-8 w-8 p-0"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Bar */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          <Input
            placeholder="Search by name, topic, or presenter..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-5 rounded-full border-2 focus:border-primary"
          />
        </div>
        
        {/* Visibility Filter */}
        <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
          <SelectTrigger className="w-[160px] rounded-full">
            <Eye className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Visibility" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="public">Public Only</SelectItem>
            <SelectItem value="private">Hidden Only</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Department Filter */}
        <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
          <SelectTrigger className="w-[180px] rounded-full">
            <Building className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map(dept => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Sort */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[160px] rounded-full">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="most_viewed">Most Viewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {sortedPresentations.length > 0 && (
        <Card className="bg-gray-50">
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
              >
                {selectedIds.length === filteredPresentations.length && filteredPresentations.length > 0 ? (
                  <CheckSquare className="w-5 h-5 text-primary" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
                {selectedIds.length > 0 
                  ? `${selectedIds.length} selected` 
                  : 'Select All'
                }
              </button>
            </div>
            
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('activate')}
                  disabled={bulkLoading}
                  className="rounded-full"
                >
                  {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                  Activate ({selectedIds.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('deactivate')}
                  disabled={bulkLoading}
                  className="rounded-full text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                  Hide ({selectedIds.length})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds([])}
                  className="rounded-full"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sortedPresentations.length === 0 ? (
        <div className="text-center py-20">
          <Library className="w-16 h-16 text-[#9CA3AF] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1A1A1A]">No presentations found</h3>
          <p className="text-[#5C5C5C] mt-2">
            {searchQuery ? "Try a different search term" : "Presentations will appear here once completed"}
          </p>
        </div>
      ) : (
        /* Gallery by Month */
        <div className="space-y-8">
          {sortedMonths.map(month => (
            <div key={month}>
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-[#1A1A1A]">
                  {new Date(month + "-01").toLocaleDateString([], { month: 'long', year: 'numeric' })}
                </h2>
                <span className="text-sm text-[#5C5C5C] bg-gray-100 px-2 py-0.5 rounded-full">
                  {groupedByMonth[month].length} presentations
                </span>
              </div>
              
              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {groupedByMonth[month].map(presentation => (
                    <Card 
                      key={presentation.id}
                      className={`group overflow-hidden transition-all duration-300 cursor-pointer border-2 ${
                        selectedIds.includes(presentation.id) ? 'border-primary bg-primary/5' : 'hover:border-primary/30'
                      } ${presentation.is_public === false ? 'opacity-60' : ''}`}
                    >
                      {/* Checkbox */}
                      <div className="absolute top-3 left-3 z-10">
                        <Checkbox
                          checked={selectedIds.includes(presentation.id)}
                          onCheckedChange={() => handleSelect(presentation.id)}
                          className="bg-white"
                        />
                      </div>
                      
                      {/* Thumbnail */}
                      <div className="aspect-video bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB] relative overflow-hidden group/thumb">
                        {presentation.first_slide_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${presentation.first_slide_url}`}
                            alt={presentation.topic_name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Image className="w-12 h-12 text-[#9CA3AF]" />
                          </div>
                        )}
                        
                        {/* Quick Preview overlay */}
                        <div 
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center cursor-pointer z-10"
                          onClick={(e) => { e.stopPropagation(); openQuickPreview(presentation); }}
                          data-testid="quick-preview-overlay"
                        >
                          <div className="bg-white rounded-full p-3 shadow-lg">
                            <Play className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        
                        {/* Visibility badge */}
                        <div className={`absolute top-2 right-2 ${presentation.is_public === false ? 'bg-orange-500' : 'bg-green-500'} text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-20`}>
                          {presentation.is_public === false ? (
                            <><EyeOff className="w-3 h-3" /> Hidden</>
                          ) : (
                            <><Eye className="w-3 h-3" /> Public</>
                          )}
                        </div>
                        
                        {/* View count badge */}
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-20">
                          <Eye className="w-3 h-3" />
                          {presentation.view_count || 0}
                        </div>
                        
                        {/* Score badge */}
                        {presentation.overall_score && (
                          <div className={`absolute bottom-2 left-2 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-20 ${
                            presentation.overall_score >= 80 ? 'bg-emerald-500' :
                            presentation.overall_score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                          }`}>
                            <Award className="w-3 h-3" />
                            {presentation.overall_score}
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-[#1A1A1A] line-clamp-1">
                              {presentation.topic_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-2 text-xs text-[#9CA3AF]">
                              <User className="w-3 h-3" />
                              <span className="truncate">{presentation.user_name}</span>
                            </div>
                          </div>
                          
                          {/* Actions dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/session/${presentation.id}/replay`}>
                                  <Play className="w-4 h-4 mr-2" /> Watch Recording
                                </Link>
                              </DropdownMenuItem>
                              {presentation.status === 'analyzed' && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/session/${presentation.id}/feedback`}>
                                    <BarChart3 className="w-4 h-4 mr-2" /> View Feedback
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => toggleVisibility(presentation.id, presentation.is_public !== false)}
                              >
                                {presentation.is_public === false ? (
                                  <><Eye className="w-4 h-4 mr-2" /> Show in Library</>
                                ) : (
                                  <><EyeOff className="w-4 h-4 mr-2" /> Hide from Library</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/library/${presentation.id}`}>
                                  <FileText className="w-4 h-4 mr-2" /> View Details
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="space-y-3">
                  {groupedByMonth[month].map(presentation => (
                    <Card 
                      key={presentation.id}
                      className={`transition-all border ${
                        selectedIds.includes(presentation.id) ? 'border-primary bg-primary/5' : 'hover:border-primary/30'
                      } ${presentation.is_public === false ? 'opacity-70' : ''}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        {/* Checkbox */}
                        <Checkbox
                          checked={selectedIds.includes(presentation.id)}
                          onCheckedChange={() => handleSelect(presentation.id)}
                        />
                        
                        {/* Thumbnail */}
                        <Link to={`/admin/library/${presentation.id}`} className="w-32 h-20 bg-[#F3F4F6] rounded-lg overflow-hidden flex-shrink-0">
                          {presentation.first_slide_url ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${presentation.first_slide_url}`}
                              alt={presentation.topic_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-8 h-8 text-[#9CA3AF]" />
                            </div>
                          )}
                        </Link>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#1A1A1A]">
                              {presentation.topic_name}
                            </h3>
                            <Badge variant={presentation.is_public === false ? "outline" : "default"} className="text-xs">
                              {presentation.is_public === false ? 'Hidden' : 'Public'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-[#9CA3AF]">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {presentation.user_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {presentation.department || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(presentation.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {presentation.view_count || 0} views
                            </span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Link to={`/session/${presentation.id}/replay`}>
                            <Button
                              variant="default"
                              size="sm"
                              className="rounded-full"
                              data-testid="watch-recording-btn"
                            >
                              <Play className="w-4 h-4 mr-1" /> Watch
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openQuickPreview(presentation)}
                            className="rounded-full"
                            data-testid="quick-preview-btn"
                          >
                            Preview
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleVisibility(presentation.id, presentation.is_public !== false)}
                            className="rounded-full"
                          >
                            {presentation.is_public === false ? (
                              <Eye className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </Button>
                          <Link to={`/admin/library/${presentation.id}`}>
                            <Button variant="outline" size="sm" className="rounded-full">
                              <FileText className="w-4 h-4 mr-1" /> Details
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick Preview Modal */}
      {previewSession && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-[#1A1A1A]">{previewSession.topic_name}</h2>
                <p className="text-sm text-[#5C5C5C]">{previewSession.user_name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeQuickPreview} className="rounded-full">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4 bg-slate-100">
              {previewLoading ? (
                <div className="flex items-center justify-center h-96">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : previewFileType === 'pdf' && previewPdfUrl ? (
                <div className="flex items-center justify-center">
                  <Document
                    file={previewPdfUrl}
                    onLoadSuccess={onPreviewDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center h-96">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    }
                    error={
                      <div className="text-center py-20 text-red-500">
                        <p>Failed to load PDF. Try opening in a new tab.</p>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={previewPage} 
                      scale={previewScale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      className="shadow-lg"
                    />
                  </Document>
                </div>
              ) : previewSlides.length > 0 ? (
                <div className="flex items-center justify-center">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${previewSlides[previewPage - 1]}`}
                    alt={`Slide ${previewPage}`}
                    className="max-w-full max-h-[60vh] object-contain shadow-lg rounded-lg"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-96 text-[#5C5C5C]">
                  <p>No preview available for this presentation</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer - Navigation */}
            <div className="flex items-center justify-between p-4 border-t bg-white">
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPreviewScale(prev => Math.max(0.5, prev - 0.2))}
                  className="rounded-full"
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-[#5C5C5C] w-16 text-center">{Math.round(previewScale * 100)}%</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPreviewScale(prev => Math.min(2.5, prev + 0.2))}
                  className="rounded-full"
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  onClick={() => changePreviewPage(-1)}
                  disabled={previewPage <= 1}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <span className="text-sm font-medium">
                  {previewPage} / {previewFileType === 'pdf' ? (previewNumPages || '?') : previewSlides.length}
                </span>
                <Button 
                  variant="outline" 
                  onClick={() => changePreviewPage(1)}
                  disabled={previewPage >= (previewFileType === 'pdf' ? previewNumPages : previewSlides.length)}
                  className="rounded-full"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <Link to={`/admin/library/${previewSession.id}`}>
                <Button variant="default" className="rounded-full">
                  View Full Details
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
